import { APPLICATION_DOCUMENTS_MAX } from 'pages/ApplicationDetailsPage/constants/applicationDocumentCatalog'

// Local frontend review only.
// If you are using a real local or hosted backend, remove
// REACT_APP_USE_FIXTURES=true from .env.development.local and restart npm start.
export const shouldUseApplicationFlowFixtures = () =>
	process.env.NODE_ENV === 'development' &&
	process.env.REACT_APP_USE_FIXTURES === 'true'

const attentionDemoApp = (id, targetId, university, overrides = {}) => ({
	id,
	applicationTargetId: targetId,
	studentId: 'fixture-student-1',
	studentName: 'Aarav Mehta',
	universityId: `fixture-university-${id}`,
	university,
	country: 'US',
	location: 'United States',
	major: 'Computer Science',
	applicationPortal: 'Common App',
	intake: 'Fall 2026',
	applicationStatus: 'in_progress',
	stage: 'Essays',
	essayProgress: '1/1',
	deadline: '2026-11-01',
	result: 'draft',
	documentsCount: 6,
	missingDocuments: 1,
	newDocumentsCount: 0,
	feedbackCount: 0,
	docsUpdatesCount: 0,
	addedAt: '2026-03-15',
	expandedDocuments: [
		{ id: 'doc-transcript', name: 'Official Transcripts', tone: 'green', status: '✓' },
		{ id: 'doc-resume', name: 'Resume', tone: 'green', status: '✓' },
		{ id: 'doc-missing', name: 'Financial Aid Form', tone: 'red', status: '?' },
	],
	expandedEssays: [
		{
			id: `essay-${id}`,
			name: 'Personal Statement',
			type: 'common',
			title: 'Personal Statement',
			status: overrides.essay || overrides.essayStatus || 'draft',
			updatedAt: '2026-05-20',
			prompt: 'Attention column design preview essay.',
			content: 'Fixture content for layout review.',
		},
	],
	expandedLorRequests: [],
	...overrides,
})

const applications = [
	{
		id: 'fixture-app-1',
		applicationTargetId: 'fixture-target-1',
		studentId: 'fixture-student-1',
		studentName: 'Aarav Mehta',
		universityId: 'fixture-university-mit',
		university: 'Massachusetts Institute of Technology',
		country: 'US',
		location: 'United States',
		major: 'Computer Science',
		applicationPortal: 'Common App',
		intake: 'Fall 2026',
		applicationStatus: 'in_progress',
		stage: 'Essays',
		essay: 'revision_requested',
		essayStatus: 'revision_requested',
		feedbackCount: 2,
		docsUpdatesCount: 2,
		newDocumentsCount: 2,
		essayProgress: '2/3',
		deadline: '2026-11-01',
		result: 'draft',
		documentsCount: 12,
		missingDocuments: 5,
		addedAt: '2026-03-15',
		expandedDocuments: [
			{ id: 'doc-transcript', name: 'Official Transcripts', tone: 'green', status: '✓' },
			{ id: 'doc-resume', name: 'Resume / Activities List', tone: 'green', status: '✓' },
			{ id: 'doc-passport', name: 'Passport Copy', tone: 'green', status: '✓' },
			{ id: 'doc-certificate', name: 'Grade 10 Certificate', tone: 'green', status: '✓' },
			{ id: 'doc-midyear', name: 'Mid-Year Report', tone: 'green', status: '✓' },
			{ id: 'doc-school-profile', name: 'School Profile', tone: 'green', status: '✓' },
			{ id: 'doc-lor', name: 'Recommendation Letter', tone: 'green', status: '✓' },
			{ id: 'doc-financial', name: 'Financial Aid Form', tone: 'red', status: '?' },
			{ id: 'doc-bank', name: 'Bank Statement', tone: 'red', status: '?' },
			{ id: 'doc-test-score', name: 'SAT Score Report', tone: 'red', status: '?' },
			{ id: 'doc-portfolio', name: 'Project Portfolio', tone: 'red', status: '?' },
			{ id: 'doc-id-photo', name: 'Passport Size Photo', tone: 'orange', status: '?' },
		],
		expandedEssays: [
			{
				id: 'essay-common-app',
				name: 'Common App Essay',
				status: 'draft',
				prompt:
					'Draft focuses on how Aarav took ownership of a difficult transition and rebuilt confidence after a weak semester.',
				content:
					'My first real setback came when a semester that had always felt predictable suddenly stopped making sense. I had built so much of my confidence around always performing well that I did not know how to respond when that pattern broke. What changed me was not the grade itself but the process of rebuilding trust in my own effort through smaller routines, honest conversations, and learning how to ask for help.',
			},
			{
				id: 'essay-why-us',
				name: 'Why This University?',
				status: 'review',
				prompt:
					'Ready for counselor review. It covers faculty fit, one research lab, and one student organization.',
				content:
					'I am drawn to this university because it treats computer science as a way to build with people, not just for them. The combination of hands-on research, interdisciplinary labs, and a student culture that values making things public matches the way I already like to learn. I can picture myself contributing to one lab, one student group, and one mentoring space from my first year.',
			},
			{
				id: 'essay-community',
				name: 'Community Impact',
				status: 'final',
				prompt:
					'Final version describes a peer mentoring project, attendance gains, and what Aarav learned from leading it.',
				content:
					'When I started mentoring younger students in our coding club, I thought my job was to explain concepts clearly. I eventually realized that what mattered more was making them feel that they belonged in the room. By shifting our sessions toward collaboration and shared demos, participation grew, attendance stabilized, and I learned how leadership often begins with making space for other people to speak.',
			},
		],
		expandedLorRequests: [
			{ id: 'lor-smith', teacher: 'Dr. Smith', date: 'Oct 12', status: 'Pending' },
			{ id: 'lor-counselor', teacher: 'Counselor Yash', date: 'Oct 14', status: 'Done', isYou: true },
			{ id: 'lor-white', teacher: 'Mr. White', date: 'Oct 15', status: 'Pending' },
		],
	},
	{
		id: 'fixture-app-2',
		applicationTargetId: 'fixture-target-2',
		studentId: 'fixture-student-1',
		studentName: 'Aarav Mehta',
		universityId: 'fixture-university-uw',
		university: 'University of Washington',
		country: 'US',
		location: 'United States',
		major: 'Data Science',
		applicationPortal: 'Common App',
		intake: 'Fall 2026',
		applicationStatus: 'ready_to_apply',
		stage: 'Documents',
		essay: 'submitted_for_review',
		essayStatus: 'submitted_for_review',
		feedbackCount: 2,
		docsUpdatesCount: 0,
		essayProgress: '2/3',
		deadline: '2026-10-15',
		result: 'inReview',
		documentsCount: 5,
		missingDocuments: 1,
		addedAt: '2026-03-18',
		expandedDocuments: [
			{ id: 'doc-transcript', name: 'Official Transcripts', tone: 'green', status: '✓' },
			{ id: 'doc-resume', name: 'Resume', tone: 'green', status: '✓' },
			{ id: 'doc-score', name: 'SAT Score Report', tone: 'orange', status: '?' },
		],
		expandedEssays: [
			{
				id: 'essay-common-app',
				name: 'Common App Essay',
				status: 'review',
				prompt: 'Counselor review is in progress for the main essay.',
				content:
					'The current draft has a strong emotional arc and a clear sense of growth, but it still needs one more grounded scene. Right now the opening establishes motivation, yet the middle paragraph would benefit from a concrete example that shows what changed in practice and how that shift shaped later decisions.',
			},
			{
				id: 'essay-supplement',
				name: 'Supplemental Essay',
				status: 'final',
				prompt: 'Final version is ready for submission.',
				content:
					'This version is concise and specific. It names the program features that matter, explains how they connect to the student’s existing interests, and closes with a believable picture of what contribution the student hopes to make on campus.',
			},
			{
				id: 'essay-activity',
				name: 'Activity Essay',
				status: 'draft',
				prompt: 'Student still needs to add one concrete example.',
				content:
					'The structure is working, but the final paragraph still reads too generally. Adding one short moment from the activity itself would make the leadership point feel earned instead of summarized.',
			},
		],
		expandedLorRequests: [
			{ id: 'lor-patel', teacher: 'Ms. Patel', date: 'Sep 28', status: 'Done' },
			{ id: 'lor-wilson', teacher: 'Mr. Wilson', date: 'Oct 02', status: 'Pending', isYou: true },
		],
	},
	attentionDemoApp('fixture-app-1-att-draft', 'fixture-target-1-att-draft', 'Stanford University', {
		essay: 'draft',
		essayStatus: 'draft',
		applicationStatus: 'not_started',
	}),
	attentionDemoApp('fixture-app-1-att-approved', 'fixture-target-1-att-approved', 'Georgia Institute of Technology', {
		essay: 'approved',
		essayStatus: 'approved',
		applicationStatus: 'accepted',
	}),
	attentionDemoApp(
		'fixture-app-1-att-revision',
		'fixture-target-1-att-revision',
		'Carnegie Mellon University',
		{
			essay: 'revision_requested',
			essayStatus: 'revision_requested',
		},
	),
	attentionDemoApp(
		'fixture-app-1-att-pending',
		'fixture-target-1-att-pending',
		'California Institute of Technology',
		{
			essay: 'submitted_for_review',
			essayStatus: 'submitted_for_review',
			applicationStatus: 'submitted',
		},
	),
	attentionDemoApp('fixture-app-1-att-docs', 'fixture-target-1-att-docs', 'Cornell University', {
		essay: 'draft',
		essayStatus: 'draft',
		docsUpdatesCount: 3,
		newDocumentsCount: 3,
	}),
	attentionDemoApp(
		'fixture-app-1-att-approved-full',
		'fixture-target-1-att-approved-full',
		'Princeton University',
		{
			essay: 'approved',
			essayStatus: 'approved',
			feedbackCount: 1,
			docsUpdatesCount: 1,
			applicationStatus: 'accepted',
		},
	),
	attentionDemoApp('fixture-app-1-att-feedback', 'fixture-target-1-att-feedback', 'Yale University', {
		essay: 'submitted_for_review',
		essayStatus: 'submitted_for_review',
		feedbackCount: 1,
	}),
	attentionDemoApp(
		'fixture-app-1-att-rev-fb',
		'fixture-target-1-att-rev-fb',
		'Columbia University',
		{
			essay: 'revision_requested',
			essayStatus: 'revision_requested',
			feedbackCount: 3,
		},
	),
	attentionDemoApp(
		'fixture-app-1-att-rev-docs',
		'fixture-target-1-att-rev-docs',
		'University of Pennsylvania',
		{
			essay: 'revision_requested',
			essayStatus: 'revision_requested',
			docsUpdatesCount: 2,
			newDocumentsCount: 2,
		},
	),
	attentionDemoApp('fixture-app-1-att-draft-fb', 'fixture-target-1-att-draft-fb', 'Duke University', {
		essay: 'draft',
		essayStatus: 'draft',
		feedbackCount: 2,
	}),
	{
		id: 'fixture-app-3',
		applicationTargetId: 'fixture-target-3',
		studentId: 'fixture-student-2',
		studentName: 'Maya Johnson',
		universityId: 'fixture-university-ucl',
		university: 'University College London',
		country: 'GB',
		location: 'United Kingdom',
		major: 'Biomedical Sciences',
		applicationPortal: 'UCAS',
		intake: 'September 2026',
		applicationStatus: 'submitted',
		stage: 'Submitted',
		essay: 'approved',
		essayStatus: 'final',
		essayProgress: '3/3',
		deadline: '2026-09-20',
		result: 'inReview',
		documentsCount: 7,
		missingDocuments: 0,
		addedAt: '2026-02-12',
		expandedDocuments: [
			{ id: 'doc-transcript', name: 'Official Transcripts', tone: 'green', status: '✓' },
			{ id: 'doc-passport', name: 'Passport', tone: 'green', status: '✓' },
			{ id: 'doc-ielts', name: 'IELTS Score Report', tone: 'green', status: '✓' },
		],
		expandedEssays: [
			{
				id: 'essay-ucas',
				name: 'UCAS Personal Statement',
				status: 'final',
				prompt: 'Final statement is submitted with biology coursework, lab exposure, and healthcare volunteering.',
				content:
					'The submitted version clearly connects academic preparation with observed clinical environments. It balances subject interest, evidence of readiness, and a grounded motivation for pursuing biomedical sciences.',
			},
		],
		expandedLorRequests: [
			{ id: 'lor-green', teacher: 'Dr. Green', date: 'Sep 04', status: 'Done' },
		],
	},
	{
		id: 'fixture-app-4',
		applicationTargetId: 'fixture-target-4',
		studentId: 'fixture-student-3',
		studentName: 'Sofia Garcia',
		universityId: 'fixture-university-nyu',
		university: 'New York University',
		country: 'US',
		location: 'United States',
		major: 'Business Analytics',
		applicationPortal: 'Common App',
		intake: 'Spring 2027',
		applicationStatus: 'not_started',
		stage: 'Planning',
		essay: 'draft_only',
		essayStatus: 'draft',
		essayProgress: '1/3',
		deadline: '2026-12-10',
		result: 'draft',
		documentsCount: 4,
		missingDocuments: 3,
		addedAt: '2026-04-02',
		expandedDocuments: [
			{ id: 'doc-transcript', name: 'Official Transcripts', tone: 'red', status: '?' },
			{ id: 'doc-resume', name: 'Resume', tone: 'orange', status: '?' },
			{ id: 'doc-financial', name: 'Financial Statement', tone: 'red', status: '?' },
		],
		expandedEssays: [
			{
				id: 'essay-personal',
				name: 'Personal Statement',
				status: 'draft',
				prompt: 'Student has started the first draft about business, data, and student leadership.',
				content:
					'The idea is strong, but the draft is still exploratory. It introduces business, analytics, and leadership as separate interests; the next revision should connect them through one project or experience that shows how they reinforce each other.',
			},
		],
		expandedLorRequests: [
			{ id: 'lor-business', teacher: 'Business Teacher', date: 'Not sent', status: 'Pending' },
		],
	},
]

const essaysByStudent = {
	'fixture-student-1': [
		{
			id: 'fixture-essay-1',
			title: 'Personal Statement',
			status: 'in_progress',
			updated_at: '2026-05-16T09:30:00.000Z',
			prompt: 'Aarav is explaining why computer science matters to him and how he wants to use it to solve practical problems.',
		},
		{
			id: 'fixture-essay-2',
			title: 'Why this university?',
			status: 'completed',
			updated_at: '2026-05-18T11:15:00.000Z',
			prompt: 'This essay connects Aaravs interests with the universitys labs, classes, and student projects.',
		},
		{
			id: 'fixture-essay-3',
			title: 'Community impact',
			status: 'draft',
			updated_at: '2026-05-10T15:45:00.000Z',
			prompt: 'Aarav is drafting a short story about mentoring younger students in a coding club.',
		},
	],
	'fixture-student-2': [
		{
			id: 'fixture-essay-4',
			title: 'UCAS Personal Statement',
			status: 'completed',
			updated_at: '2026-05-12T10:00:00.000Z',
			prompt: 'Maya has a final personal statement focused on biology coursework, lab exposure, and healthcare volunteering.',
		},
	],
	'fixture-student-3': [
		{
			id: 'fixture-essay-5',
			title: 'Personal Statement',
			status: 'draft',
			updated_at: '2026-05-08T08:20:00.000Z',
			prompt: 'Sofia is starting her first draft about business, data, and student leadership.',
		},
	],
}

const documentsByStudent = {
	'fixture-student-1': [
		{
			id: 'fixture-doc-1',
			requirementId: 'fixture-req-transcript',
			name: 'Transcript',
			status: 'uploaded',
			uploadedAt: '2026-05-15T08:00:00.000Z',
			url: '#',
		},
		{
			id: 'fixture-doc-2',
			requirementId: 'fixture-req-resume',
			name: 'Resume',
			status: 'uploaded',
			uploadedAt: '2026-05-13T13:20:00.000Z',
			url: '#',
		},
		{
			id: 'fixture-doc-3',
			requirementId: 'fixture-req-financials',
			name: 'Financial Statement',
			status: 'missing',
			uploadedAt: '',
			url: '',
		},
		{
			id: 'fixture-doc-4',
			requirementId: 'fixture-req-test-score',
			name: 'SAT Score Report',
			status: 'missing',
			uploadedAt: '',
			url: '',
		},
		{
			id: 'fixture-doc-8',
			requirementId: 'fixture-req-passport',
			name: 'Passport Copy',
			status: 'verified',
			uploadedAt: '2026-05-12T09:10:00.000Z',
			url: '#',
		},
		{
			id: 'fixture-doc-9',
			requirementId: 'fixture-req-certificate',
			name: 'Grade 10 Certificate',
			status: 'uploaded',
			uploadedAt: '2026-05-11T10:40:00.000Z',
			url: '#',
		},
		{
			id: 'fixture-doc-10',
			requirementId: 'fixture-req-bank',
			name: 'Bank Statement',
			status: 'missing',
			uploadedAt: '',
			url: '',
		},
		{
			id: 'fixture-doc-11',
			requirementId: 'fixture-req-midyear',
			name: 'Mid-Year Report',
			status: 'uploaded',
			uploadedAt: '2026-05-17T15:30:00.000Z',
			url: '#',
		},
		{
			id: 'fixture-doc-12',
			requirementId: 'fixture-req-counselor',
			name: 'Counselor School Report',
			status: 'verified',
			uploadedAt: '2026-05-18T12:05:00.000Z',
			url: '#',
		},
		{
			id: 'fixture-doc-13',
			requirementId: 'fixture-req-portfolio',
			name: 'Project Portfolio',
			status: 'missing',
			uploadedAt: '',
			url: '',
		},
		{
			id: 'fixture-doc-14',
			requirementId: 'fixture-req-photo',
			name: 'Passport Size Photo',
			status: 'uploaded',
			uploadedAt: '2026-05-19T09:45:00.000Z',
			url: '#',
		},
		{
			id: 'fixture-doc-15',
			requirementId: 'fixture-req-school-profile',
			name: 'School Profile',
			status: 'verified',
			uploadedAt: '2026-05-20T11:15:00.000Z',
			url: '#',
		},
	],
	'fixture-student-2': [
		{
			id: 'fixture-doc-5',
			requirementId: 'fixture-req-transcript',
			name: 'Transcript',
			status: 'uploaded',
			uploadedAt: '2026-05-11T08:00:00.000Z',
			url: '#',
		},
		{
			id: 'fixture-doc-6',
			requirementId: 'fixture-req-passport',
			name: 'Passport',
			status: 'uploaded',
			uploadedAt: '2026-05-12T08:00:00.000Z',
			url: '#',
		},
	],
	'fixture-student-3': [
		{
			id: 'fixture-doc-7',
			requirementId: 'fixture-req-transcript',
			name: 'Transcript',
			status: 'missing',
			uploadedAt: '',
			url: '',
		},
	],
}

const lorRequestsByStudent = {
	'fixture-student-1': [
		{
			id: 'fixture-lor-1',
			counselorDisplayName: 'Ms. Patel',
			universityName: 'Math Teacher',
			receivedLorFileId: 'fixture-lor-file-1',
			updatedAt: '2026-05-14T09:00:00.000Z',
		},
		{
			id: 'fixture-lor-2',
			counselorDisplayName: 'Mr. Wilson',
			universityName: 'Counselor Letter',
			lorType: 'email_request',
			status: 'requested',
			needsCounselorUpload: true,
			updatedAt: '2026-05-18T09:00:00.000Z',
		},
	],
	'fixture-student-2': [
		{
			id: 'fixture-lor-3',
			counselorDisplayName: 'Dr. Green',
			universityName: 'Biology Teacher',
			receivedLorFileId: 'fixture-lor-file-3',
			updatedAt: '2026-05-10T09:00:00.000Z',
		},
	],
	'fixture-student-3': [],
}

const universityById = {
	'fixture-university-1': {
		title: 'Massachusetts Institute of Technology',
		location: 'Cambridge, United States',
		country: 'USA',
		intake_session: 'Fall 2026',
		application_deadline: '2026-01-15',
		deadline_type: 'Round',
		logo_s3_link: '',
		gallery: [],
		tution_fees: '$0K-60K',
		accomodation: '-',
		rank_qs: 12,
		rank_country: '2',
		num_of_scholarship: '10',
		campuses: '2',
		university_url: 'https://example.edu',
		exams_accepted: 'IELTS, TOEFL, GRE',
		twelfth_marks: '85%',
		graduation_marks: '3.5 GPA',
		description:
			'Massachusetts Institute of Technology is a public research university. Established in 1861, it has been serving and making distinctive contributions to society and attracted the brightest students from all over the world.',
	},
	'fixture-university-mit': {
		title: 'Massachusetts Institute of Technology',
		location: 'Cambridge, United States',
		intake_session: 'Fall 2026',
		application_deadline: '2026-11-01',
		logo_s3_link: '',
		gallery: [],
		tution_fees: '$59,750',
		accomodation: '$22,000',
		rank_country: '#2 National',
		description: 'Fixture university profile for frontend review.',
	},
	'fixture-university-uw': {
		title: 'University of Washington',
		location: 'Seattle, United States',
		intake_session: 'Fall 2026',
		application_deadline: '2026-10-15',
		logo_s3_link: '',
		gallery: [],
		tution_fees: '$42,000',
		accomodation: '$18,500',
		rank_country: '#40 National',
		description: 'Fixture university profile for frontend review.',
	},
	'fixture-university-ucl': {
		title: 'University College London',
		location: 'London, United Kingdom',
		intake_session: 'September 2026',
		application_deadline: '2026-09-20',
		logo_s3_link: '',
		gallery: [],
		tution_fees: 'GBP 31,100',
		accomodation: 'GBP 15,000',
		rank_country: '#9 Global',
		description: 'Fixture university profile for frontend review.',
	},
	'fixture-university-nyu': {
		title: 'New York University',
		location: 'New York, United States',
		intake_session: 'Spring 2027',
		application_deadline: '2026-12-10',
		logo_s3_link: '',
		gallery: [],
		tution_fees: '$62,800',
		accomodation: '$23,400',
		rank_country: '#35 National',
		description: 'Fixture university profile for frontend review.',
	},
}

const studentProfiles = {
	'fixture-student-1': {
		student: {
			name: 'Aarav Mehta',
			image_url: '',
		},
	},
	'fixture-student-2': {
		student: {
			name: 'Maya Johnson',
			image_url: '',
		},
	},
	'fixture-student-3': {
		student: {
			name: 'Sofia Garcia',
			image_url: '',
		},
	},
}

const ESSAY_PRIORITY_RANK = {
	submitted_for_review: 0,
	revision_requested: 1,
	draft: 2,
	draft_only: 3,
	approved: 4,
	final: 4,
}

const aggregateFixtureEssayStatus = statuses => {
	let best = null
	let bestRank = 99
	statuses.forEach(raw => {
		const key = String(raw || '')
			.trim()
			.toLowerCase()
			.replace(/-/g, '_')
		const rank = ESSAY_PRIORITY_RANK[key]
		if (rank == null) return
		if (rank < bestRank) {
			bestRank = rank
			best = key === 'final' ? 'approved' : key
		}
	})
	return best
}

const isPrimaryFixtureTarget = app => {
	if (app.studentId !== 'fixture-student-1') return false
	const targetId = String(app.applicationTargetId || app.id || '')
	return targetId === 'fixture-target-1'
}

const normalizeFixtureApplicationRow = app => {
	const primary = isPrimaryFixtureTarget(app)
	// The required-document count is no longer fixed - it is the baseline plus
	// whatever the courses a student applied to require - so the fixture reads it
	// from the row and only falls back to the baseline size.
	const total = Number(app.documentsTotal) || APPLICATION_DOCUMENTS_MAX
	const fulfilled = Math.min(total, Number(app.documentsCount) || 0)
	return {
		id: app.id,
		applicationTargetId: app.applicationTargetId || app.id,
		studentId: app.studentId,
		studentName: app.studentName,
		universityId: app.universityId,
		university: app.university,
		country: app.country,
		major: app.major,
		applicationStatus: app.applicationStatus,
		deadline: app.deadline,
		addedAt: app.addedAt,
		feedbackCount: Number(app.feedbackCount) || 0,
		commonAppFeedbackOwner: primary,
		commonAppEssayOwner: primary,
		documentsCount: fulfilled,
		missingDocuments: Math.max(0, total - fulfilled),
		documentsTotal: total,
		newDocumentsCount: Number(app.newDocumentsCount ?? app.docsUpdatesCount) || 0,
		bragSheetExists: Boolean(app.bragSheetExists),
		bragSheetUploadedAt: app.bragSheetUploadedAt || null,
		lorRequestsCount: Number(app.lorRequestsCount) || 0,
		isNewApplication: Boolean(app.isNewApplication),
	}
}

const buildFixtureEssaySummary = () => {
	const essays = []
	const statusesByTarget = {}

	applications.forEach(app => {
		const targetId = String(app.applicationTargetId || app.id || '').trim()
		if (!targetId) return
		const primary = isPrimaryFixtureTarget(app)
		const sources = Array.isArray(app.expandedEssays) ? app.expandedEssays : []
		if (!sources.length && (app.essay || app.essayStatus)) {
			sources.push({
				id: `essay-${targetId}`,
				title: 'Personal Statement',
				type: primary ? 'common_app' : 'university',
				status: app.essay || app.essayStatus,
				updatedAt: app.addedAt || '2026-05-20',
			})
		}
		sources.forEach(entry => {
			const typeRaw = String(entry.type || entry.name || '').toLowerCase()
			const essayType =
				typeRaw.includes('common') && primary
					? 'common_app'
					: typeRaw.includes('common')
						? null
						: 'university'
			if (!essayType) return
			const essayId = String(
				entry.id || `essay-${targetId}-${essays.length}`,
			)
			const status = entry.status || 'draft'
			essays.push({
				id: essayId,
				applicationTargetId: targetId,
				studentId: app.studentId,
				title: entry.title || entry.name || 'Essay',
				type: essayType,
				status,
				updatedAt: entry.updatedAt || app.addedAt || '2026-05-20',
				displayVersionId: entry.displayVersionId || `ver-${essayId}-1`,
				versionCount: Number(entry.versionCount) || 2,
				unreadFeedbackCount: Number(entry.unreadFeedbackCount) || 0,
				isNew: Boolean(entry.isNew),
				latestVersionUpdatedAt:
					entry.latestVersionUpdatedAt ||
					entry.updatedAt ||
					app.addedAt ||
					'2026-05-20',
			})
			statusesByTarget[targetId] = statusesByTarget[targetId] || []
			statusesByTarget[targetId].push(entry.status || 'draft')
		})
	})

	const byTargetId = {}
	Object.entries(statusesByTarget).forEach(([targetId, statuses]) => {
		const aggregateStatus = aggregateFixtureEssayStatus(statuses)
		if (aggregateStatus) {
			byTargetId[targetId] = { aggregateStatus }
		}
	})

	return {
		updatedAt: new Date().toISOString(),
		essays,
		byTargetId,
	}
}

const buildFixtureAppAssignments = () => ({
	updatedAt: new Date().toISOString(),
	assignments: [
		{
			id: 'fixture-assign-essay-1',
			applicationTargetId: 'fixture-target-1',
			studentId: 'fixture-student-1',
			type: 'essay',
			status: 'in_progress',
			title: 'Personal Statement',
			startDate: '2026-03-15',
			dueDate: '2026-11-01',
		},
		{
			id: 'fixture-assign-doc-1',
			applicationTargetId: 'fixture-target-1',
			studentId: 'fixture-student-1',
			type: 'document',
			status: 'not_started',
			title: 'Financial Statement',
			startDate: '2026-03-15',
			dueDate: '2026-11-01',
		},
		{
			id: 'fixture-assign-lor-1',
			applicationTargetId: 'fixture-target-1',
			studentId: 'fixture-student-1',
			type: 'lor',
			status: 'in_progress',
			title: 'Math Teacher',
			startDate: '2026-04-01',
			dueDate: null,
		},
	],
})

export const fixtureFetchApplications = () => ({
	applications: applications.map(normalizeFixtureApplicationRow),
})

export const fixtureFetchEssaySummary = () => buildFixtureEssaySummary()

export const fixtureFetchAppAssignments = () => buildFixtureAppAssignments()

export const fixtureFetchStudentApplications = studentId => ({
	applications: applications.filter(
		app => String(app.studentId) === String(studentId),
	),
})

export const fixtureFetchStudentEssays = ({ studentId }) => ({
	data: {
		essays: essaysByStudent[String(studentId)] || [],
	},
})

export const fixtureFetchStudentApplicationDocuments = studentId => {
	const documents = documentsByStudent[String(studentId)] || []
	return { documents, groups: null }
}

export const fixtureFetchStudentLorRequests = studentId => ({
	requests: lorRequestsByStudent[String(studentId)] || [],
})

export const fixtureFetchUniversityById = universityId =>
	universityById[String(universityId)] || null

const universityCoursesById = {
	'fixture-university-1': [
		{
			id: 'fixture-course-1',
			course_id: 'fixture-course-1',
			course_name: 'BSc Computer Science',
			fees_usd: '45000',
			tution_fees: '₹58.78 L',
			ielts_band: '7.0',
			degree: 'Undergraduate',
			duration: '72 Months',
			intake_session: 'Sep 26',
			application_deadline: '2026-03-01',
			academic_discipline: 'Medicine & Pharmacy',
			sub_academic_discipline: 'Medical Sciences',
			description:
				'A comprehensive program that prepares students for a career in medicine and surgery. It combines theoretical knowledge with practical skills essential for healthcare professionals.',
			courses: ['Anatomy', 'Physiology', 'Pharmacology', 'Clinical Skills'],
			industry_alignment: '["Healthcare","Medical Research","Public Health"]',
			career_path: 'Internal Medicine Clinician, Medical Educator',
			entry_requirements:
				'Minimum grades in relevant subjects. English proficiency required.',
			requirement_boards: {
				IB: 'IB score of 36 with HL in Math and a lab science.',
				'British A-Levels': 'A*AA including Chemistry and Biology.',
			},
			parsed_exams_accepted: {
				IELTS: '7.0',
				TOEFL: '100',
			},
		},
		{
			id: 'fixture-course-2',
			course_id: 'fixture-course-2',
			course_name: 'MSc Artificial Intelligence',
			fees_usd: '52000',
			ielts_band: '7.5',
			application_deadline: '2026-02-01',
		},
	],
	'fixture-university-mit': [
		{
			id: 'fixture-mit-cs',
			course_id: 'fixture-mit-cs',
			course_name: 'BSc Computer Science',
			fees_usd: '59750',
			ielts_band: '7.0',
			application_deadline: '2026-11-01',
		},
	],
}

export const fixtureFetchUniversityCourses = universityId =>
	universityCoursesById[String(universityId)] || []

export const fixtureFetchStudentProfile = studentId =>
	studentProfiles[String(studentId)] || null

export const fixtureFetchStudentWishlist = studentId => ({
	items: [
		{
			type: 'target',
			course_id: 'fixture-course-1',
			country: 'USA',
			location: 'Boston, United States',
			rank_qs: 12,
			logo_s3_link: '',
			application_deadline: '2026-01-15',
			courses: {
				course_id: 'fixture-course-1',
				university_id: 'fixture-university-1',
				course_name: 'BSc Computer Science',
				university_name: 'Fixture University',
				country: 'USA',
				location: 'Boston, United States',
				fees_usd: '45000',
				ielts_band: '7.0',
				application_deadline: '2026-01-15',
			},
		},
	],
})
